<!--
 @description User account settings page. Allows the authenticated user to
 update their profile photo, display name, email address, password, UI
 language, email-list subscription, challenge category blacklist (up to two
 excluded), and challenge blacklist (individual challenges excluded from game
 sampling, capped at 30% of owned challenges). Each action opens a dedicated
 inline modal with appropriate validation. All Firebase write operations are
 guarded by reCAPTCHA v3. Edit controls for email and password are hidden for
 OAuth providers (Google / Facebook). Redirects unauthenticated users to /login.

 Account section (sidemenu-redesign): a signed-in user sees a coral "Sign out"
 pill at the bottom of the page. Tapping it opens a sign-out confirmation modal
 with role="alertdialog" (DEC-015). On confirmation, the existing project sign-out
 flow runs; focus returns to the pill on cancel or auth failure (DEC-019, DEC-024).
 The section is absent from the DOM for anonymous users (authStore.firebaseUser gate,
 AC-13, EC-09 — reacts without route reload if auth state flips mid-session).

 @props
   noBackground — When true, removes the page background style. Use when
                  embedding inside a full-screen modal (e.g. from GameNavbar).

 @example
 Route: /parameters (name: 'parameters')
 RouterView / (rendered automatically by the router)

 When embedded inside GameNavbar's full-screen modal:
 <Parameters :noBackground="true" />

 @accessibility The sign-out confirmation modal uses dialogRole="alertdialog" so
 assistive technologies announce it as requiring an explicit user choice (AC-20,
 C-08). aria-labelledby and aria-describedby fall through to the native <dialog>
 via Vue's inheritAttrs: true (A11Y-02, DEC-024).
-->
<template>
  <div class="parameters" :class="noBackground ? 'parameters--no-background' : ''">
    <h2 class="primary">{{ $t('pages.parameters.title') }}</h2>

    <div class="parameters__user-card">
      <div class="parameters__user-image">
        <i class="fi fi-sr-pencil tooltip" @click="openChangeImageModal">
          <span class="tooltiptext parameters__tooltiptext--lower">{{ $t('global.edit') }}</span>
        </i>
        <i v-if="user?.photoURL" class="fi fi-ss-trash tooltip" @click="openDeleteImageModal">
          <span class="tooltiptext parameters__tooltiptext--lower">{{ $t('global.delete') }}</span>
        </i>
        <img
          :src="
            user?.photoURL
              ? user?.photoURL
              : firebaseUser?.photoURL
                ? firebaseUser.photoURL
                : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'
          "
          alt="User"
          class="parameters__user-image"
        />
      </div>
      <div class="parameters__user-infos">
        <div class="parameters__user-name">
          <p>
            {{
              user?.displayName
                ? user?.displayName
                : firebaseUser?.displayName
                  ? firebaseUser?.displayName
                  : ''
            }}
          </p>
          <i class="fi fi-sr-pencil tooltip" @click="openChangeDisplayNameModal">
            <span class="tooltiptext parameters__tooltiptext--lower">{{ $t('global.edit') }}</span>
          </i>
        </div>
        <div class="parameters__user-email">
          <p>{{ user?.email ? user.email : firebaseUser?.email }}</p>
          <i
            v-if="
              firebaseUser?.providerData[0].providerId !== 'google.com' &&
              firebaseUser?.providerData[0].providerId !== 'facebook.com'
            "
            class="fi fi-sr-pencil tooltip"
            @click="openChangeEmailModal"
          >
            <span class="tooltiptext parameters__tooltiptext--lower">{{ $t('global.edit') }}</span>
          </i>
        </div>
        <button
          v-if="
            firebaseUser?.providerData[0].providerId !== 'google.com' &&
            firebaseUser?.providerData[0].providerId !== 'facebook.com'
          "
          class="tertiary parameters__change-password"
          @click="openResetPasswordModal"
        >
          {{ $t('pages.parameters.change-password') }}
        </button>
      </div>
    </div>

    <div class="parameters__other-options">
      <SelectVue
        id="language"
        :title="$t('global.language')"
        :items="languages"
        @update:modelValue="updateLanguage"
        :validation="{ required: true }"
        :touched="true"
        :placeholder="getPlaceholderLanguage()"
      />
      <Switch
        id="email-list"
        :title="$t('pages.parameters.email-list-subscription')"
        :modelValue="emailList"
        @update:modelValue="openEmailListModal"
      />
    </div>

    <div class="parameters__challenge-buttons">
      <button class="tertiary" @click="openSelectCategoriesModal">
        {{ $t('pages.parameters.change-challenges') }}
      </button>

      <button v-if="authStore.isAuthenticated" class="tertiary" @click="openBlacklistModal">
        {{ $t('challengeBlacklist.settingsRowLabel') }}
      </button>
    </div>

    <div class="useful-links parameters__useful-links">
      <router-link :to="{ name: 'disclaimer' }" aria-label="Disclaimer">{{
        $t('pages.parameters.disclaimer')
      }}</router-link>
      <a
        :href="locale === 'fr' ? HOW_TO_INSTALL_LINK_FR : HOW_TO_INSTALL_LINK_EN"
        aria-label="How to install"
        class="install"
        target="_blank"
        >{{ $t('pages.parameters.how-to-install') }}
        <i class="fi fi-rr-arrow-up-right-from-square"></i>
      </a>
    </div>

    <!-- Account section — only rendered for signed-in users (AC-11, AC-13, EC-09) -->
    <div v-if="authStore.firebaseUser" class="parameters-account-section">
      <h3 class="parameters-account-section__heading">
        {{ $t('pages.parameters.account.title') }}
      </h3>
      <button
        ref="signOutPillRef"
        type="button"
        class="primary full-width parameters-account-section__sign-out"
        @click="openSignOutModal"
      >
        {{ $t('pages.parameters.account.signOut') }}
      </button>
    </div>

    <!--
      Sign-out confirmation modal (AC-14, AC-15, AC-16, AC-17, AC-20).
      Uses role="alertdialog" so assistive technologies announce this as a
      destructive confirmation requiring an explicit user choice (C-08, DEC-015).
      isOutsideClosable=true (the default) means clicking outside acts as Cancel.
      The native <dialog> cancel event (ESC) is forwarded by Modal.vue via @close,
      which runs cancelSignOut and returns focus to the sign-out pill (AC-17).
      aria-labelledby and aria-describedby fall through to the native <dialog>
      element (Vue inheritAttrs: true default) providing the required accessible
      name and description for the alertdialog role (A11Y-02, RESP-04, DEC-024).
    -->
    <Modal
      :isOpen="signOutModalOpen"
      dialogRole="alertdialog"
      :isOutsideClosable="true"
      :isClosable="false"
      @close="cancelSignOut"
      aria-labelledby="sign-out-modal-title"
      aria-describedby="sign-out-modal-body"
      class="parameters__modal"
    >
      <div class="parameters__sign-out-confirm">
        <h2 id="sign-out-modal-title">{{ $t('pages.parameters.account.confirmTitle') }}</h2>
        <p id="sign-out-modal-body">{{ $t('pages.parameters.account.confirmBody') }}</p>
        <div class="parameters__row parameters__buttons">
          <button
            type="button"
            class="primary parameters-account-section__modal-btn"
            @click="confirmSignOut"
          >
            {{ $t('pages.parameters.account.signOut') }}
          </button>
          <button
            type="button"
            class="parameters-account-section__modal-btn"
            @click="cancelSignOut"
          >
            {{ $t('global.cancel') }}
          </button>
        </div>
      </div>
    </Modal>

    <Modal
      :fullScreen="selectCategoriesOpen || blacklistModalIsOpen"
      :isOpen="modalIsOpen"
      @close="closeModal"
      :isOutsideClosable="false"
      class="parameters__modal"
    >
      <div class="parameters__image-uploader" v-if="imageModalIsOpen">
        <FileUpload
          id="user-image-uploader"
          :title="$t('pages.parameters.image-uploader')"
          :validation="{ required: true }"
          :maxFiles="1"
          @update:modelValue="changeImage"
        />
      </div>
      <div class="parameters__display-name" v-if="displayNameModalIsOpen">
        <Input
          id="user-display-name"
          :title="$t('pages.parameters.display-name')"
          :placeholder="$t('pages.parameters.display-name-placeholder')"
          :value="nameValue"
          :validation="{ required: true, length: { min: 2, max: 25 } }"
          @update:modelValue="updateName"
        />
      </div>
      <div class="parameters__email" v-if="emailModalIsOpen">
        <Input
          type="email"
          id="user-email"
          :title="$t('global.form.email')"
          :placeholder="$t('global.form.email-placeholder')"
          :value="emailValue"
          :validation="{
            required: true,
            regex: { value: EMAIL_REGEX, message: EMAIL_REGEX_MESSAGE }
          }"
          @update:modelValue="updateEmail"
        />
      </div>
      <div class="parameters__select-categories" v-if="selectCategoriesOpen">
        <h2>{{ $t('pages.parameters.change-challenges') }}</h2>
        <CategoryToggle
          :disabled="blacklistedCategories.length === 2 && isCategoryToggled(CategoryTypes.GAME)"
          :category="CategoryTypes.GAME"
          :isToggled="isCategoryToggled(CategoryTypes.GAME)"
          :avatarType="AvatarTypes.ORANGE_GREEN"
          @categoryToggled="toggleCategory($event.value, CategoryTypes.GAME)"
        />
        <CategoryToggle
          :disabled="
            blacklistedCategories.length === 2 && isCategoryToggled(CategoryTypes.MOVEMENT)
          "
          :category="CategoryTypes.MOVEMENT"
          :isToggled="isCategoryToggled(CategoryTypes.MOVEMENT)"
          :avatarType="AvatarTypes.BLUE_YELLOW"
          @categoryToggled="toggleCategory($event.value, CategoryTypes.MOVEMENT)"
        />
        <CategoryToggle
          :disabled="blacklistedCategories.length === 2 && isCategoryToggled(CategoryTypes.OUTSIDE)"
          :category="CategoryTypes.OUTSIDE"
          :isToggled="isCategoryToggled(CategoryTypes.OUTSIDE)"
          :avatarType="AvatarTypes.RED_BLUE"
          @categoryToggled="toggleCategory($event.value, CategoryTypes.OUTSIDE)"
        />
        <CategoryToggle
          :disabled="
            blacklistedCategories.length === 2 && isCategoryToggled(CategoryTypes.PHYSICAL)
          "
          :category="CategoryTypes.PHYSICAL"
          :isToggled="isCategoryToggled(CategoryTypes.PHYSICAL)"
          :avatarType="AvatarTypes.PURPLE_YELLOW"
          @categoryToggled="toggleCategory($event.value, CategoryTypes.PHYSICAL)"
        />
        <CategoryToggle
          :disabled="
            blacklistedCategories.length === 2 && isCategoryToggled(CategoryTypes.CONSTRAINT)
          "
          :category="CategoryTypes.CONSTRAINT"
          :isToggled="isCategoryToggled(CategoryTypes.CONSTRAINT)"
          :avatarType="AvatarTypes.BLUE_RED"
          @categoryToggled="toggleCategory($event.value, CategoryTypes.CONSTRAINT)"
        />
        <CategoryToggle
          :disabled="blacklistedCategories.length === 2 && isCategoryToggled(CategoryTypes.PROBLEM)"
          :category="CategoryTypes.PROBLEM"
          :isToggled="isCategoryToggled(CategoryTypes.PROBLEM)"
          :avatarType="AvatarTypes.GREEN_YELLOW"
          @categoryToggled="toggleCategory($event.value, CategoryTypes.PROBLEM)"
        />
        <CategoryToggle
          :disabled="blacklistedCategories.length === 2 && isCategoryToggled(CategoryTypes.TIME)"
          :category="CategoryTypes.TIME"
          :isToggled="isCategoryToggled(CategoryTypes.TIME)"
          :avatarType="AvatarTypes.ORANGE_GREEN"
          @categoryToggled="toggleCategory($event.value, CategoryTypes.TIME)"
        />
        <p class="primary">
          {{ $t('pages.parameters.max-category-blacklisted') }}
        </p>
      </div>
      <div class="parameters__delete" v-if="imageDeleteModalIsOpen">
        <h2>{{ $t('pages.parameters.remove-image') }}</h2>
        <p>{{ $t('pages.parameters.remove-image-confirmation') }}</p>
      </div>
      <div class="parameters__delete" v-if="resetPasswordModalIsOpen">
        <h2>{{ $t('pages.parameters.reset-password') }}</h2>
        <p>{{ $t('pages.parameters.reset-password-confirmation') }}</p>
      </div>
      <div class="parameters__delete" v-if="emailListModalIsOpen">
        <h2>{{ $t('pages.parameters.email-list') }}</h2>
        <p>{{ $t('pages.parameters.remove-email-list-confirmation') }}</p>
      </div>
      <BlacklistedChallengesModal
        v-if="blacklistModalIsOpen"
        :isOpen="blacklistModalIsOpen"
        :cap="blacklistCap"
      />
      <div v-if="!blacklistModalIsOpen" class="parameters__row parameters__buttons">
        <button :class="{ primary: !imageDeleteModalIsOpen }" @click="closeModal()">
          {{ $t('global.cancel') }}
        </button>
        <button
          :class="{
            disabled:
              (imageModalIsOpen && !imageStatus) ||
              (displayNameModalIsOpen && !nameStatus) ||
              (emailModalIsOpen && !emailStatus) ||
              isVerifying,
            primary: imageDeleteModalIsOpen,
            secondary: !imageDeleteModalIsOpen
          }"
          @click="submitForm"
        >
          {{
            isVerifying
              ? $t('global.verifying')
              : imageDeleteModalIsOpen
                ? $t('global.delete')
                : resetPasswordModalIsOpen
                  ? $t('pages.parameters.reset-password')
                  : $t('global.confirm')
          }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import Modal from '@/components/common/Global/modal/Modal.vue'
import BlacklistedChallengesModal from '@/views/Website/parameters/BlacklistedChallengesModal.vue'
import FileUpload from '@/components/common/form/file-upload/FileUpload.vue'
import Input from '@/components/common/form/input/Input.vue'
import Switch from '@/components/common/form/switch/Switch.vue'
import SelectVue from '@/components/common/form/select/Select.vue'
import CategoryToggle from '@/components/common/category-toggle/CategoryToggle.vue'

import type { User, Status, Item } from '@/types/Types'
import { CategoryTypes, AvatarTypes } from '@/types/Enums'
import {
  EMAIL_REGEX,
  EMAIL_REGEX_MESSAGE,
  HOW_TO_INSTALL_LINK_EN,
  HOW_TO_INSTALL_LINK_FR
} from '@/types/Constants'

import { ref, computed, nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { type User as FirebaseUser } from 'firebase/auth'
import authService from '@/stores/authService'
import {
  changeUserImage,
  changeUserDisplayName,
  toggleEmailList,
  changeUserLanguage,
  updateCategoryBlacklist
} from '@/stores/users'
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/authStore'
import { useChallengeStore } from '@/stores/challengesStore'
import { useRecaptchaGuard } from '@/composables/useRecaptchaGuard'

/** Props for the Parameters view. */
interface ParametersProps {
  /** When true, removes the outer container background — use when embedding
   *  this view inside a layout that provides its own background. */
  noBackground?: boolean
}

defineProps<ParametersProps>()

const authStore = useAuthStore()
const challengeStore = useChallengeStore()
const toast = useToast()
const router = useRouter()
const { isVerifying, withRecaptcha } = useRecaptchaGuard()

const firebaseUser = ref<FirebaseUser | null>()
const user = ref<User | null>()
const modalIsOpen = ref(false)
const imageModalIsOpen = ref(false)
const imageUrl = ref('')
const imageStatus = ref(false)
const imageDeleteModalIsOpen = ref(false)
const displayNameModalIsOpen = ref(false)
const nameValue = ref('')
const nameStatus = ref(false)
const emailModalIsOpen = ref(false)
const emailValue = ref('')
const emailStatus = ref(false)
const resetPasswordModalIsOpen = ref(false)
const emailList = ref(false)
const emailListHasChanged = ref(false)
const emailListModalIsOpen = ref(false)
const selectCategoriesOpen = ref(false)
const blacklistedCategories = ref<string[]>([])
const blacklistModalIsOpen = ref(false)

/** Controls the sign-out confirmation modal (AC-14, AC-16, AC-17). */
const signOutModalOpen = ref(false)

/**
 * Ref to the sign-out pill `<button>` element.
 * Used to return focus to the pill after the confirmation modal closes,
 * per AC-17 and good a11y hygiene (AC-17 cancel / dismiss path).
 */
const signOutPillRef = ref<HTMLButtonElement | null>(null)

/**
 * Cap for the challenge blacklist: 30% of the full challenge catalogue. All packs
 * are free, so the cap is based on every challenge rather than an owned subset.
 * The backend does not persist cap to the user document (confirmed from handler.ts);
 * we mirror its formula using the locally loaded challenge catalogue length.
 * When the catalogue is empty (not yet loaded), the cap defaults to 0.
 */
const blacklistCap = computed(() => Math.floor(0.3 * challengeStore.challenges.length))

const languages = ref<Item[]>([
  { id: 'en', value: 'en', label: 'English', checked: false },
  { id: 'fr', value: 'fr', label: 'Français', checked: false }
])

const _isMobile = () => window.innerWidth <= 768

function openChangeImageModal() {
  modalIsOpen.value = true
  imageModalIsOpen.value = true
}

function openChangeDisplayNameModal() {
  modalIsOpen.value = true
  displayNameModalIsOpen.value = true
}

function openChangeEmailModal() {
  modalIsOpen.value = true
  emailModalIsOpen.value = true
}

function openDeleteImageModal() {
  modalIsOpen.value = true
  imageDeleteModalIsOpen.value = true
}

function openResetPasswordModal() {
  modalIsOpen.value = true
  resetPasswordModalIsOpen.value = true
}

function openEmailListModal(value: boolean) {
  emailList.value = value
  if (authStore.dbUser?.emailList) {
    modalIsOpen.value = true
    emailListModalIsOpen.value = true
  } else switchEmailList()
}

function openSelectCategoriesModal() {
  modalIsOpen.value = true
  selectCategoriesOpen.value = true
}

async function openBlacklistModal() {
  modalIsOpen.value = true
  blacklistModalIsOpen.value = true
  // Load the full challenge catalogue so the modal can resolve blacklisted ids
  // to their localized titles (resolveTitle) and so blacklistCap reflects the
  // owned-challenge count rather than 0. Guarded against the persisted store so
  // we don't re-read the whole collection on every open.
  if (challengeStore.challenges.length === 0) {
    try {
      await challengeStore.fetchAllChallenges(challengeStore)
    } catch {
      // Catalogue load failed — the modal falls back to raw ids (EC-06) and
      // the user can still remove entries. Do not block opening the modal.
    }
  }
}

/**
 * Opens the sign-out confirmation modal (AC-14).
 * No sign-out has occurred when this returns — the modal is only opened.
 */
function openSignOutModal() {
  signOutModalOpen.value = true
}

/**
 * Performs the actual sign-out after the user confirms in the modal (AC-16).
 * On success: navigate to home. No toast is shown on success (spec Out of scope,
 * scenario "User confirms sign-out": "no additional toast is shown").
 * On error: show the legacy error toast (EC-10 — no new error handling) and
 * return focus to the sign-out pill so keyboard/AT users can continue (A11Y-03,
 * DEC-024). Mirrors the cancel-path focus return in cancelSignOut (DEC-019).
 */
async function confirmSignOut() {
  signOutModalOpen.value = false
  try {
    await authService.signOut()
    router.push({ name: 'home' })
  } catch {
    toast.error(t('nav.messages.logout-error'))
    await nextTick()
    signOutPillRef.value?.focus()
  }
}

/**
 * Closes the sign-out confirmation modal without signing out (AC-17).
 * Returns focus to the sign-out pill after the DOM settles so keyboard
 * and screen-reader users can continue navigating from where they were.
 */
async function cancelSignOut() {
  signOutModalOpen.value = false
  await nextTick()
  signOutPillRef.value?.focus()
}

function toggleCategory(isToggled: boolean, category: string) {
  if (blacklistedCategories.value.length === 2 && !isToggled) return

  isToggled
    ? blacklistedCategories.value.splice(blacklistedCategories.value.indexOf(category), 1)
    : blacklistedCategories.value.push(category)
}

function isCategoryToggled(category: string) {
  return !blacklistedCategories.value.includes(category)
}

async function closeModal() {
  if (!emailListHasChanged.value && emailListModalIsOpen.value) {
    emailList.value = !emailList.value
  }
  modalIsOpen.value = false
  imageModalIsOpen.value = false
  displayNameModalIsOpen.value = false
  emailModalIsOpen.value = false
  imageDeleteModalIsOpen.value = false
  resetPasswordModalIsOpen.value = false
  emailListModalIsOpen.value = false
  imageStatus.value = false
  nameStatus.value = false
  emailStatus.value = false
  selectCategoriesOpen.value = false
  blacklistModalIsOpen.value = false
}

async function changeImage(fileURL: string) {
  imageUrl.value = fileURL
  imageStatus.value = imageUrl.value !== ''
}

function updateName(name: string, status: Status) {
  nameValue.value = name
  nameStatus.value = status.valid
}

function updateEmail(email: string, status: Status) {
  emailValue.value = email
  emailStatus.value = status.valid
}

function recaptchaActionForOpenModal(): string {
  if (displayNameModalIsOpen.value) return 'change_name'
  if (emailModalIsOpen.value) return 'change_email'
  if (imageModalIsOpen.value) return 'change_image'
  if (imageDeleteModalIsOpen.value) return 'delete_image'
  if (resetPasswordModalIsOpen.value) return 'reset_password'
  if (emailListModalIsOpen.value) return 'email_subscription'
  return 'user_action'
}

async function applyOpenModalAction(user: FirebaseUser) {
  if (displayNameModalIsOpen.value && nameStatus.value) {
    await changeUserDisplayName(user.uid, nameValue.value)
    authStore.updateDbUser(authStore.$state)
    toast.success(t('pages.parameters.display-name-changed'))
  } else if (emailModalIsOpen.value && emailStatus.value) {
    authService.updateEmail(emailValue.value, toast, t)
    authStore.updateDbUser(authStore.$state)
  } else if (imageModalIsOpen.value && imageStatus.value) {
    authStore.updateDbUser(authStore.$state)
    await changeUserImage(user.uid, imageUrl.value, authStore.dbUser?.photoURL)
    toast.success(t('pages.parameters.image-changed'))
  } else if (imageDeleteModalIsOpen.value) {
    await changeUserImage(user.uid, '', authStore.dbUser?.photoURL)
    authStore.updateDbUser(authStore.$state)
    toast.success(t('pages.parameters.image-removed'))
  } else if (resetPasswordModalIsOpen.value && user.email) {
    authService.sendResetPasswordEmail(user.email, toast, t)
    toast.success(t('pages.parameters.reset-password-email-sent'))
  } else if (emailListModalIsOpen.value) {
    await switchEmailList()
  } else if (selectCategoriesOpen.value) {
    await updateCategoryBlacklist(user.uid, blacklistedCategories.value)
    authStore.updateDbUser(authStore.$state)
    toast.success(t('pages.parameters.categories-updated'))
  }
}

async function submitForm() {
  if (!firebaseUser.value) return
  const user = firebaseUser.value

  const verified = await withRecaptcha(recaptchaActionForOpenModal(), () =>
    applyOpenModalAction(user)
  )

  if (verified) closeModal()
}

async function switchEmailList() {
  if (!firebaseUser.value || !authStore.dbUser) return
  await toggleEmailList(firebaseUser.value.uid, !authStore.dbUser?.emailList)
  authStore.updateDbUser(authStore.$state)
  emailListHasChanged.value = true
}

let languageHasBeenUpdated = false

function updateLanguage(value: Item[]) {
  if (languageHasBeenUpdated) {
    languageHasBeenUpdated = false
    return
  }
  const checkedItem = value.find((item) => item.checked)
  if (!checkedItem) return
  locale.value = checkedItem.value
  localStorage.setItem('locale', locale.value)
  if (!firebaseUser.value) return
  changeUserLanguage(firebaseUser.value?.uid, checkedItem.value)
  toast.success(t('pages.parameters.language-updated'))
  languageHasBeenUpdated = true
  languages.value = value
}

function getPlaceholderLanguage() {
  if (authStore.dbUser?.preferredLanguage)
    return authStore.dbUser?.preferredLanguage === 'fr' ? 'Français' : 'English'

  return locale.value === 'fr' ? 'Français' : 'English'
}

onMounted(async () => {
  firebaseUser.value = authStore.firebaseUser
  user.value = authStore.dbUser
  if (!firebaseUser.value || !authStore.dbUser) {
    router.push('/login')
    return
  }
  authStore.updateDbUser(authStore.$state)
  emailList.value = authStore.dbUser?.emailList ?? false
  blacklistedCategories.value = authStore.dbUser?.categoryBlacklist ?? []
})
</script>

<style scoped lang="scss">
.parameters {
  @extend %flex-column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0 3rem 0;
  margin: 4rem auto 7rem auto;
  max-width: 800px;
  background-color: var(--background-light);
  clip-path: polygon(15% 0, 85% 0, 100% 3%, 100% 97%, 90% 100%, 10% 100%, 0 97%, 0 3%);

  &--no-background {
    background-color: transparent;
    clip-path: none;
  }
}

// Stack the challenge-settings buttons and give them equal width. The container
// shrinks to its widest child (fit-content) and stretch makes both buttons match
// that width — no fixed width needed, so it adapts to translated label lengths.
.parameters__challenge-buttons {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: fit-content;
  max-width: 80%;
}

.parameters__user-card,
.parameters__other-options,
.parameters__useful-links {
  @extend %flex;
  padding: $double-spacing;
  border-radius: $large-border-radius;
  height: fit-content;
  width: 80%;
  margin-top: $double-spacing;
}

.parameters__user-image {
  position: relative;
  width: 100px;
  height: 100px;

  .fi-sr-pencil,
  .fi-ss-trash {
    position: absolute;
    top: 0;
    right: 0;
  }

  .fi-ss-trash {
    top: $double-spacing;
    right: -$base-spacing;
  }

  img {
    border-radius: 50%;
    object-fit: cover;
  }
}

.parameters__user-name,
.parameters__user-email {
  @extend %flex-end;
  gap: $base-spacing;

  p {
    font-size: $font-size-xl;
    margin: 0;
  }
}

.parameters__user-email {
  margin-top: $font-size-xl;

  p {
    font-size: $base-font-size;
  }
}

.parameters__change-password {
  width: 50%;
  margin-top: $double-spacing;

  @include breakpoint-md {
    width: 80%;
  }
}

.parameters__user-infos {
  @extend %flex-column;
  justify-content: center;
  align-items: flex-end;
}

.parameters__useful-links {
  padding: $base-spacing 0;

  @include breakpoint-md {
    display: flex;
    flex-direction: column;
    gap: $base-spacing;
  }

  a {
    margin: 0 auto;
    text-decoration: none;
    text-align: center;
    color: var(--primary);
    border: 0px !important;
    padding-bottom: 0px !important;

    &:hover {
      text-decoration: underline;
    }

    @include breakpoint-md {
      text-decoration: underline;
    }
  }
}

.parameters__modal {
  width: 100%;
  padding: 3rem 1rem;

  @include breakpoint-md {
    max-width: none;
    width: 100% !important;
    max-width: none;

    padding: 2rem 0;
  }
}

.parameters__buttons {
  width: calc(100% - 2rem);
  max-width: 600px;
  margin: 0 auto;
  padding-bottom: 3rem;
}

.parameters__select-categories {
  max-width: 600px;
  margin: 0 auto;

  h2,
  p {
    text-align: center;
  }

  h2 {
    margin-bottom: 0;
  }

  @include breakpoint-md {
    padding: 3rem 1rem 0 1rem;
  }
}

.parameters-account-section {
  @extend %flex-column;
  align-items: center;
  width: 80%;
  margin-top: $double-spacing;
  gap: $base-spacing;

  &__heading {
    margin: 0;
    font-size: $base-font-size;
    font-weight: $bold;
    color: var(--text);
    text-align: left;
    width: 100%;
  }

  &__sign-out {
    // Ensure the pill meets the 44 px touch target requirement (AC-12).
    // The base button mixin sets height: 2.5rem (40 px); override to minimum
    // accessible touch target height.
    min-height: 44px;
    width: 100%;
    margin: 0;
  }

  // RESP-02 (cycle-scoped fix): the two buttons inside the sign-out confirmation
  // modal inherit the global button mixin's `height: 2.5rem` (40 px), which is
  // 4 px below the 44 px WCAG 2.5.5 touch-target floor. Apply a local min-height
  // override here rather than changing the global mixin (which would affect every
  // button in the app and is out of cycle scope — see DEC-022).
  &__modal-btn {
    min-height: 44px;
  }
}

// flaticon icon selectors — kept with original fi-* class names (global classes
// from flaticon CDN) plus scoped positioning/styling rules.
.fi-sr-pencil,
.fi-ss-trash {
  border-radius: 50%;
  padding: $half-spacing 0.6rem 0.3rem 0.6rem;
  cursor: pointer;
  z-index: 1;
  background-color: var(--tertiary);
  transform: scale(0.8);
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: var(--black);
    color: var(--tertiary);
    transform: scale(1);
  }
}

.fi-ss-trash:hover {
  color: var(--danger);
}

.parameters__tooltiptext--lower {
  transform: translate(3px, 18px);
}

.parameters__row {
  @extend %flex-center;
  gap: $base-spacing;

  button {
    width: 100%;
    justify-content: center;
  }
}

.parameters__delete {
  text-align: center;
}

.parameters__sign-out-confirm {
  text-align: center;
}

@include breakpoint-md {
  .parameters__user-card,
  .parameters__other-options {
    flex-direction: column !important;
    padding: $base-spacing !important;
  }

  .parameters__user-image {
    margin-bottom: 1rem;
  }

  .parameters__user-infos {
    align-items: center !important;
    justify-content: center !important;
  }

  .parameters__user-name {
    justify-content: center !important;
  }

  .parameters__user-email {
    margin-top: 0 !important;
    justify-content: center !important;
    text-align: center !important;
  }

  .parameters {
    padding: $base-spacing 0;
    margin-left: $base-spacing;
    margin-right: $base-spacing;
    width: calc(100% - $double-spacing);
  }

  .parameters__modal {
    // width: calc(100% - 6rem) !important;

    .parameters__row {
      gap: $half-spacing !important;

      button {
        margin: 0.2rem 0 !important;
        height: 3rem;
      }
    }
  }
}
</style>
